import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Clock, User, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import KioskLayout from "@/components/KioskLayout";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KioskCheckin() {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    const storedPatient = sessionStorage.getItem("patient");
    const storedAppointment = sessionStorage.getItem("selectedAppointment");
    
    if (!storedPatient || !storedAppointment) {
      toast.error("Sessão expirada. Por favor, identifique-se novamente.");
      navigate("/identify/checkin");
      return;
    }
    
    setPatient(JSON.parse(storedPatient));
    setAppointment(JSON.parse(storedAppointment));
  }, [navigate]);

  const handleCheckin = async () => {
    if (!appointment || !patient) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/checkin`, {
        appointment_id: parseInt(appointmentId),
        patient_id: patient.id || patient.paciente_id,
      });

      if (response.data.success) {
        // Clear session data
        sessionStorage.removeItem("patient");
        sessionStorage.removeItem("selectedAppointment");
        navigate("/success/checkin");
      } else {
        toast.error("Erro ao realizar check-in. Por favor, tente novamente.");
      }
    } catch (error) {
      console.error("Checkin error:", error);
      toast.error("Erro ao realizar check-in. Por favor, dirija-se à recepção.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[0]}/${parts[1]}/${parts[2]}`;
    }
    return dateStr;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return timeStr.slice(0, 5);
  };

  if (!appointment || !patient) {
    return (
      <KioskLayout showBack step="Confirmação">
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </KioskLayout>
    );
  }

  return (
    <KioskLayout showBack step="Confirmação">
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <div className="w-24 h-24 rounded-full bg-[#00357C]/10 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-[#00357C]" strokeWidth={1.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4 font-['Outfit'] tracking-tight">
              Confirmar Check-in
            </h2>
            <p className="text-xl md:text-2xl text-slate-500">
              Verifique os dados e confirme sua presença
            </p>
          </div>

          {/* Appointment Card */}
          <div className="bg-slate-50 rounded-3xl p-8 mb-8 text-left">
            <h3 className="text-2xl font-semibold text-slate-800 mb-6 text-center">
              {appointment.especialidade || appointment.specialty || "Consulta"}
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-xl">
                <div className="w-12 h-12 rounded-full bg-[#00357C]/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#00357C]" />
                </div>
                <div>
                  <p className="text-slate-500 text-base">Paciente</p>
                  <p className="text-slate-800 font-medium">{patient.nome || patient.name}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xl">
                <div className="w-12 h-12 rounded-full bg-[#00357C]/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#00357C]" />
                </div>
                <div>
                  <p className="text-slate-500 text-base">Profissional</p>
                  <p className="text-slate-800 font-medium">
                    {appointment.profissional || appointment.professional || "Não informado"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xl">
                <div className="w-12 h-12 rounded-full bg-[#00357C]/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#00357C]" />
                </div>
                <div>
                  <p className="text-slate-500 text-base">Data</p>
                  <p className="text-slate-800 font-medium">
                    {formatDate(appointment.data || appointment.date)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xl">
                <div className="w-12 h-12 rounded-full bg-[#00357C]/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#00357C]" />
                </div>
                <div>
                  <p className="text-slate-500 text-base">Horário</p>
                  <p className="text-slate-800 font-medium">
                    {formatTime(appointment.hora || appointment.time)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            <Button
              data-testid="confirm-checkin-btn"
              onClick={handleCheckin}
              disabled={loading}
              className="kiosk-primary-btn w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                  Confirmando...
                </span>
              ) : (
                "Confirmar Presença"
              )}
            </Button>
            
            <Button
              data-testid="cancel-checkin-btn"
              onClick={() => navigate("/")}
              variant="outline"
              className="kiosk-secondary-btn w-full"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </KioskLayout>
  );
}
